(async () => {
    const { Core } = await importModule('Core');
    const { Routes, parseHTML } = await importModule('Utils');
    
    const CART_R_SECTION = 'r_cart';
    const NOTIFICATION_SECTION = 'r_cart-notification';
    const ATTR_IS_CART_PAGE = 'cart-page';
    const SECTION_DRAWER = 'g_cart-drawer';

    customElements.define('cart-provider', class extends Core {
        subscriptions = {
            'cart:add-feature': '_addFeature',
            'cart:add': '_add',
            'cart:change': '_update'
        }

        features = {};

        render() {
            this.subscribe('cart:add-feature', { global: true });
            this.subscribe('cart:add', { global: true });
            this.subscribe('cart:change', { global: true });
        }

        _addFeature({ feature }) {
            this.features[feature] = true;
        }

        _openCartDrawerImmediately(source) {
            if (!this.features['drawer'] || this.isCartPage) return;
            if (source === 'options-list' || source === 'express-option-list') return;

            window.setTimeout(() => {
                this.publish('modal:open', {
                    stash: 'cart-drawer',
                    animation: 'slideRight',
                    position: 'center-right',
                    height: '100%',
                    width: 'min(30rem, 95vw)',
                    transition: '300'
                });
            }, 0);
        }
        
        async _add({ items, source }) {
            let showNotice = null; // To store any notice message
            try {
                this._openCartDrawerImmediately(source);

                const resBody = {items};
                resBody.sections = [CART_R_SECTION]; 
                resBody.sections_url = Routes('cart'); 

                if (this.features['notification']) {
                    resBody.sections.push(NOTIFICATION_SECTION);
                    resBody.attributes = {
                        notification_items_variant_ids: items.map(({id}) => id).join(',')
                    };
                }
                
                const addResponse = await this._requestCart('cartAdd', resBody);
                const addCartData = await addResponse.json();

                this._cleanCartAttributes(); // Clean attributes regardless

                // Check for quantity errors even if response might be ok:true
                if (!addResponse.ok || (addCartData.description || addCartData.message)) {
                    // Store the notice message if present
                    showNotice = addCartData.description || addCartData.message || 'Cart Error';
                    // If the add failed or had a notice, the cart wasn't updated correctly by the add call.
                    // We MUST fetch the current state to update the UI accurately.
                    console.warn('Add failed or had notice, fetching definitive cart state.', showNotice);
                }

                // Always fetch the definitive cart state after an add attempt to ensure UI consistency
                const finalCartResponse = await fetch(Routes('cart') + '.js');
                if (!finalCartResponse.ok) {
                    throw new Error('Failed to fetch final cart state.');
                }
                const finalCartState = await finalCartResponse.json();

                // Show notice if one was generated from the add attempt
                if (showNotice) {
                     this.publish('toast-notification:open', { title: 'Notice', message: showNotice, type: 'warning' });
                }

                // === Update UI Based on FINAL Cart State ===
                if (source === 'options-list') {
                    // Source is options list: Update drawer/counts from final state, don't open drawer/redirect
                    const eventData = {};
                    if (this.features['cart-total'] || this.features['counter'] || this.features['drawer']) {
                        // Use final cart state for meta counts
                        eventData.meta = { count: finalCartState.item_count, total: finalCartState.total_price }; 
                    }
                    if (this.features['drawer']) {
                        // Fetch fresh drawer section HTML based on final state (required by cart-drawer.js)
                        eventData.drawer = { 
                            section: await this._getDrawerSection(), // Re-fetch drawer section HTML
                            open: false 
                        };
                    }
                    this.publish('cart:update', eventData);

                    // Dispatch sticky button event
                    const optionsListElement = document.querySelector(`product-options-list product-to-cart[variant-id="${items[0]?.id}"]`)?.closest('product-options-list');
                    optionsListElement?.dispatchEvent(new CustomEvent('options-list:added', { bubbles: true }));
                } else if (source === 'express-option-list' || source === 'product-card' || source === 'cart-recommendation') {
                    const eventData = {};
                    if (this.features['cart-total'] || this.features['counter'] || this.features['drawer']) {
                        eventData.meta = { count: finalCartState.item_count, total: finalCartState.total_price };
                    }
                    if (this.features['drawer']) {
                        eventData.drawer = {
                            section: await this._getDrawerSection(),
                            open: false
                        };
                    }
                    this.publish('cart:update', eventData);
                } else {
                    // Default behavior: Check redirect based on final state, publish full update
                    if (this.redirectToCart && !showNotice) { 
                        window.location.pathname = Routes('cart');
                        return; 
                    }
                    // If not redirecting (either conditions not met or a notice was shown), publish updates
                    const eventData = {};
                    if (this.features['cart-total'] || this.features['counter'] || this.features['drawer']) {
                        // Use final cart state for meta counts
                        eventData.meta = { count: finalCartState.item_count, total: finalCartState.total_price };
                    }
                    if (this.features['drawer']) {
                        eventData.drawer = { 
                            section: await this._getDrawerSection(), 
                            open: false 
                        };
                    }
                    // Add notification if applicable (using original addCartData for product ID)
                    if (this.features['notification']) {
                        eventData.notification = { 
                           recommendationProductId: addCartData.items?.[0]?.product_id 
                        };
                    }
                    if (this.features['cart']) {
                        eventData.cart = this._createCartSection(addCartData.sections?.[CART_R_SECTION]);
                    }
                    this.publish('cart:update', eventData);
                }
                // ==========================================

            } catch (e) {
                console.error('Cart Provider _add Error:', e);
                // Keep existing error handling for genuine errors
                const errorMessage = (e instanceof Error) ? e.message : 'An unknown error occurred';
                let parsedMessage = {};
                try { parsedMessage = JSON.parse(errorMessage); } catch (_) {}
                this.publish(
                    'toast-notification:open', 
                    {   
                        title: parsedMessage.message || 'Error', 
                        message: parsedMessage.description || ((typeof errorMessage === 'string') ? errorMessage : 'Failed to update cart.'), 
                        type: 'error' 
                    }
                )
                this.publish('cart:error');
            }  
        }

        _cleanCartAttributes() {
            if(this.features['notification']) {
                this._requestCart('cartUpdate', { attributes: { notification_items_variant_ids: null }});
            }
        }

        async _update({ key, quantity, selling_plan }) {
            try {
                const changeResponse = await this._requestCart('cartChange', {
                    id: key, quantity, selling_plan,
                    sections: [CART_R_SECTION],
                    sections_url: Routes('cart')
                });

                const changeCartData = await changeResponse.json();

                if (!changeResponse.ok) {
                    throw new Error(changeCartData.description || changeCartData.message || 'Failed to change cart');
                }
                
                // Fetch the definitive final cart state AFTER the change
                const finalCartResponse = await fetch(Routes('cart') + '.js');
                if (!finalCartResponse.ok) {
                    throw new Error('Failed to fetch final cart state after change.');
                }
                const finalCartState = await finalCartResponse.json();

                // Manually build and publish the update event using final state
                // This replaces the call to _publishUpdates
                const eventData = {};  
                if (this.features['cart-total'] || this.features['counter'] || this.features['drawer']) {
                    eventData.meta = { count: finalCartState.item_count, total: finalCartState.total_price };
                }
                if (this.features['drawer']) {
                    eventData.drawer = { 
                        section: await this._getDrawerSection(), // Re-fetch drawer section
                        open: !this.features['notification'] // Default drawer open behavior on change
                    };
                }
                if (this.features['notification']) {
                    // If notifications are needed on change, logic might need adding here
                    // based on changeResponse data or finalCartState
                }
                if (this.features['cart']) {
                    eventData.cart = this._createCartSection(changeCartData.sections?.[CART_R_SECTION]);
                }
                this.publish('cart:update', eventData);

            } catch (e) {
                 console.error('Cart Provider _update Error:', e);
                 // Publish error notification
                 this.publish('toast-notification:open', { title: 'Error', message: e.message, type: 'error' });
                 this.publish('cart:error');
            }
        }
        
        async _getCartMeta() {
            const res = await fetch(`${Routes('cart')}?view=meta`);
            const { count, ...data } = await res.json();
            return { count: Number(count), ...data };
        }

        async _getDrawerSection() {
            const res = await fetch(`${Routes('root')}?section_id=${SECTION_DRAWER}`);
            return await res.text();
        }

        _createCartSection(sectionHTML) {
            if (!sectionHTML) return null;
            const cartSection = parseHTML(sectionHTML);
            return {
                getCount: () => Number(cartSection.querySelector('[data-cart-counter]')?.textContent || 0),
                getItems: () => cartSection.querySelector('[data-cart-items]') || document.createDocumentFragment(),
                getSubtotalBlock: () => cartSection.querySelector('[data-cart-subtotal-block]') || document.createDocumentFragment(),
                getFreeShippingBlock: () => cartSection.querySelector('[data-cart-free-shipping-block]') || document.createDocumentFragment()
            };
        }

        _requestCart(route, body) {
            return fetch(`${Routes(route)}.js`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(body)
            })
        }

        get isCartPage() {
            return this.hasAttribute(ATTR_IS_CART_PAGE)
        }

        get redirectToCart() {
            return false;
        }
    });

})();
